import { ILastEstimateResponse, IApptTypeEstimationLinesInput, IApptTypeEstimationLinesResponse, IEstimationLine, IEstimationPartial, 
    IEstimationLineResponse, IEstimationLineDeleteInputs, IEstimationGeneralResponse, IEstimationLineUpdateInputs, 
    IEstimationDeleteInputs, IEstimationApptTypeCPTCountResponse, IEstimationPatientPayerResponse, IEstimationFutureAppointmentResponse,
    IEstimationChargeAllowedInputs, IEstimationChargeAllowedAggregateResponse, IEstimationChargeAllowedFeeScheduleResponse,
    IEstimationEligServiceTypeResponse, IEstimationLastEligCheckResponse, IEstimationGetEstimateById,
    IEstimationSaveCalculationInputs, IEstimationHistoryResponse, IEstimationSaveToMmlInputs, IEstimationSaveToMmlResponse,
    IEstimationAppointmentInfoResponse, IEstimationPdfReportKeyInfo, IEstimationConvertToHmtlParams, IEstimationChargeAllowedAggregateInputs,
    IEstimationMmlSignedEstimateData, ISelectedEstimate, IEstimateAutoGenerateSetup, IDefaultProviderSetup, IDefaultLocationSetup,
    IDefaultApptStatusSetup, IDefaultApptTypeSetup, IDefaultResourceSetup, IAutoGenerateLogResults } from './Models/PatientEstimatorModel';
import { getJson, postJson, getVoid } from './ChoiceApi';
import { ICompanySetting } from './Models/CompanySettingModel';

export const addEstimationLinesByApptType = {
    async getEstimateLines(payload: IApptTypeEstimationLinesInput): Promise<Array<IApptTypeEstimationLinesResponse>> {
        return (await postJson("api/Patient/CostEstimator/GetEstimationLinesByApptType", payload)) as Array<IApptTypeEstimationLinesResponse>;   
    }    
};

export const costEstimator = {
    async lastEstimate(patientId: number): Promise<ILastEstimateResponse> {
        const result = await getJson(`api/Patient/CostEstimator/LastEstimate/${patientId}`);
        return result as ILastEstimateResponse;
    },

    async getPrintableRazorView(estimationId: number): Promise<string|undefined> {
        return await getVoid(`officemd/eligibility/printablecostestimate/${estimationId}`);
    },

    async convertHtmlToPdf(payload: IEstimationConvertToHmtlParams): Promise<IEstimationPdfReportKeyInfo> {
        return (await postJson("api/Patient/CostEstimator/ConvertHtmlToPdf", payload)) as IEstimationPdfReportKeyInfo;   
    },

    async getEstimateLinesById(estimationId: number): Promise<IEstimationLineResponse[]> {
        const result = await getJson(`api/Patient/CostEstimator/GetEstimateLinesById/${estimationId}`);
        return result as IEstimationLineResponse[]
    },

    async getApptTypeCPTCount(): Promise<IEstimationApptTypeCPTCountResponse> {
        const result = await getJson(`api/Patient/CostEstimator/GetApptTypeCPTCount`);
        return result as IEstimationApptTypeCPTCountResponse;
    },

    async getPatientPayers(patientId: number): Promise<IEstimationPatientPayerResponse[]> {
        const result = await getJson(`api/Patient/CostEstimator/GetPatientPayers/${patientId}`);
        return result as IEstimationPatientPayerResponse[];
    },

    async getFutureAppointments(patientId: number): Promise<IEstimationFutureAppointmentResponse[]> {
        const result = await getJson(`api/Patient/CostEstimator/GetFutureAppointments/${patientId}`);
        return result as IEstimationFutureAppointmentResponse[];
    },

    async getCompanySetting(parameterName: string, parameterOrder: number): Promise<ICompanySetting> {
        const result = await getJson(`api/CompanySetting/Get/${parameterName}/${parameterOrder}`);
        return result as ICompanySetting;
    },

    async getChargeAllowedByAggregate(payload: IEstimationChargeAllowedAggregateInputs): Promise<IEstimationChargeAllowedAggregateResponse> {
        return (await postJson("api/Patient/CostEstimator/GetChargeAllowedAggregate", payload)) as IEstimationChargeAllowedAggregateResponse;   
    },

    async getChargeAllowedByFeeSchedule(payload: IEstimationChargeAllowedInputs): Promise<IEstimationChargeAllowedFeeScheduleResponse> {
        return (await postJson("api/Patient/CostEstimator/GetChargeAllowedFeeSchedule", payload)) as IEstimationChargeAllowedFeeScheduleResponse;   
    },

    async getEligServiceTypeList(): Promise<IEstimationEligServiceTypeResponse[]> {
        return (await getJson(`api/Patient/CostEstimator/GetEligServiceTypes`)) as IEstimationEligServiceTypeResponse[];
    },

    async get271ResponseServiceTypesList(patientTransactionId: number): Promise<IEstimationEligServiceTypeResponse[]> {
        return (await getJson(`api/Patient/CostEstimator/Get271ResponseServiceTypes/${patientTransactionId}`)) as IEstimationEligServiceTypeResponse[];
    },

    async getLastEligCheck(patientId: number, payerEntityId: number): Promise<IEstimationLastEligCheckResponse> {
        return (await getJson(`api/Patient/CostEstimator/GetLastEligCheck/${patientId}/${payerEntityId}`)) as IEstimationLastEligCheckResponse;
    },

    async getMmlSignedEstimateData(estimationId: number): Promise<IEstimationMmlSignedEstimateData | undefined> {
        return await getJson(`api/Patient/CostEstimator/GetMMLSignedEstimate/${estimationId}`);
    },

    async getEstimationById(estimationId: number): Promise<IEstimationGetEstimateById> {
        return (await getJson(`api/Patient/CostEstimator/GetEstimationById/${estimationId}`)) as IEstimationGetEstimateById;
    },

    async get271Response(patientTransactionId: number, serviceTypeCode: string): Promise<IEstimationGetEstimateById> {
        return (await getJson(`api/Patient/CostEstimator/Get271Response/${patientTransactionId}/${serviceTypeCode}`)) as IEstimationGetEstimateById;
    },

    async getEstimateHistory(patientId: number): Promise<IEstimationHistoryResponse[]> {
        const result = await getJson(`api/Patient/CostEstimator/GetEstimateHistory/${patientId}`);
        return result as IEstimationHistoryResponse[];
    },

    async getAppointmentInfo(appointmentId: number): Promise<IEstimationAppointmentInfoResponse> {
        return (await getJson(`api/Patient/CostEstimator/GetAppointmentInfo/${appointmentId}`)) as IEstimationAppointmentInfoResponse;
    },

    async getAutoGenerateEstimateSetupSettings(): Promise<IEstimateAutoGenerateSetup> {
        return (await getJson(`api/Patient/CostEstimator/GetAutoGenerateEstimateSetupSettings`)) as IEstimateAutoGenerateSetup;
    },

    async getAutoGenerateLogResults(daysBack: number): Promise<IAutoGenerateLogResults[]> {
        return (await getJson(`api/Patient/CostEstimator/GetAutoGenerateLogResults/${daysBack}`)) as IAutoGenerateLogResults[];
    }
};

export const saveEstimation = {
    async saveEstimatePartial(payload: IEstimationPartial): Promise<IEstimationGetEstimateById> {
        return (await postJson("api/Patient/CostEstimator/SaveEstimate", payload)) as IEstimationGetEstimateById;   
    }, 
    
    async saveEstimateLine(payload: IEstimationLine): Promise<IEstimationLineResponse[]> {
        return (await postJson("api/Patient/CostEstimator/SaveEstimateLine", payload)) as IEstimationLineResponse[];   
    },
    
    async updateEstimateLine(payload: IEstimationLineUpdateInputs): Promise<IEstimationLineResponse[]> {
        return (await postJson("api/Patient/CostEstimator/UpdateEstimateLine", payload)) as IEstimationLineResponse[];   
    },

    async savePatientBenefitManualEntry(payload: IEstimationGetEstimateById): Promise<IEstimationGetEstimateById> {
        return (await postJson("api/Patient/CostEstimator/SavePatientBenefitManualEntry", payload)) as IEstimationGetEstimateById;   
    },

    async saveCalculation(payload: IEstimationSaveCalculationInputs): Promise<IEstimationGetEstimateById> {
        return (await postJson("api/Patient/CostEstimator/SaveCalculation", payload)) as IEstimationGetEstimateById;   
    },

    async saveEstimateToMml(payload: IEstimationSaveToMmlInputs): Promise<IEstimationSaveToMmlResponse> {
        return (await postJson("api/Patient/CostEstimator/SaveEstimateToMml", payload)) as IEstimationSaveToMmlResponse;   
    },

    async acceptEstimateById(payload: ISelectedEstimate): Promise<IEstimationGeneralResponse> {
        return (await postJson("api/Patient/CostEstimator/AcceptEstimateById", payload)) as IEstimationGeneralResponse;   
    },

    async saveAutoGenerateSetupProvider(payload: IDefaultProviderSetup): Promise<IEstimationGeneralResponse> {
        return (await postJson("api/Patient/CostEstimator/SaveAutoGenerateSetupProvider", payload)) as IEstimationGeneralResponse;   
    },

    async saveAutoGenerateSetupLocation(payload: IDefaultLocationSetup): Promise<IEstimationGeneralResponse> {
        return (await postJson("api/Patient/CostEstimator/SaveAutoGenerateSetupLocation", payload)) as IEstimationGeneralResponse;   
    },

    async saveAutoGenerateSetupApptStatus(payload: IDefaultApptStatusSetup): Promise<IEstimationGeneralResponse> {
        return (await postJson("api/Patient/CostEstimator/SaveAutoGenerateSetupApptStatus", payload)) as IEstimationGeneralResponse;   
    },

    async saveAutoGenerateSetupApptType(payload: IDefaultApptTypeSetup): Promise<IEstimationGeneralResponse> {
        return (await postJson("api/Patient/CostEstimator/SaveAutoGenerateSetupApptType", payload)) as IEstimationGeneralResponse;   
    },

    async saveAutoGenerateSetupResource(payload: IDefaultResourceSetup): Promise<IEstimationGeneralResponse> {
        return (await postJson("api/Patient/CostEstimator/SaveAutoGenerateSetupResource", payload)) as IEstimationGeneralResponse;   
    }
};

export const deleteOperations = {
    async deleteEstimationLine(payload: IEstimationLineDeleteInputs): Promise<IEstimationGeneralResponse> {
        return (await postJson("api/Patient/CostEstimator/DeleteEstimateLine", payload)) as IEstimationGeneralResponse;   
    },

    async deleteEstimation(payload: IEstimationDeleteInputs): Promise<IEstimationGeneralResponse> {
        return (await postJson("api/Patient/CostEstimator/DeleteEstimate", payload)) as IEstimationGeneralResponse;   
    }

};
